import React, { useEffect, useRef, useState } from "react";
import ReactJson from "@microlink/react-json-view";

import ErrorIcon from "@mui/icons-material/Error";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DataObjectIcon from "@mui/icons-material/DataObject";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Slide from "@mui/material/Slide";

import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";

import MapFullScreen from "../../map/mapFullScreen";

import useOpenLayersMap from "../../hooks/useOpenLayersMap";

import { getDateRangeFromFlight, getFlightHistoryFromData, handleDownloadOperations } from "./opUtil";
import { ConvertCmpVolumesToFeatures } from "../../map/mapUtil";
import { getHoursMinutesAndSecondsFromIsoString } from "../../util";

import { useUserAuth } from "../../contexts/authContext";

const FlightHistoryDialog = ({ selectedFlightHistoryFlight, setSelectedFlightHistoryFlight }) => {
    const [flightHistory, setFlightHistory] = useState([]);
    const [flightHistoryLoading, setFlightHistoryLoading] = useState(true);
    const [flightHistoryErrorText, setFlightHistoryErrorText] = useState("");

    const [selectedFlightHistoryEventIndex, setSelectedFlightHistoryEventIndex] = useState(0);
    const [selectedFlightJson, setSelectedFlightJson] = useState(null);

    const vectorSourceRef = useRef(null);
    const vectorLayerRef = useRef(null);
    const selectedItemRef = useRef(null);

    const { userMapSettings } = useUserAuth();

    const MAP_ID = "flight-history-map";
    const map = useOpenLayersMap(MAP_ID, selectedFlightHistoryFlight);

    useEffect(() => {
        if (map && selectedFlightHistoryFlight) {
            handleFetchFlightHistory(selectedFlightHistoryFlight);
        }
    }, [map, selectedFlightHistoryFlight]);

    useEffect(() => {
        if (selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [selectedFlightHistoryEventIndex]);

    const handleFetchFlightHistory = async (flight) => {
        try {
            setFlightHistoryLoading(true);

            const requestOptions = { method: "GET", headers: { "Content-Type": "application/json" } };
            const response = await fetch(`/api/op/flightHistory/get/${flight.flight_uuid}`, requestOptions);

            if (!response.ok) throw new Error(`An error occured while loading the flight history: ${response.statusText}`);
            const data = await response.json();

            const sortedData = [...data].sort((a, b) => {
                return new Date(a.date_changed).getTime() - new Date(b.date_changed).getTime();
            });
            const newFlightHistory = getFlightHistoryFromData(sortedData);
            setFlightHistory(newFlightHistory);

            const startingIndex = 0;
            handleSelectedFlightIndexChange(startingIndex, newFlightHistory);
        } catch (err) {
            console.error(err);
            setFlightHistoryErrorText("An error occured while loading the flight history. Please try again later");
        } finally {
            setFlightHistoryLoading(false);
        }
    };
    const handleSelectedFlightIndexChange = (index, history) => {
        setSelectedFlightHistoryEventIndex(index);

        if (!vectorSourceRef.current) {
            vectorSourceRef.current = new VectorSource();
        }
        if (!vectorLayerRef.current) {
            vectorLayerRef.current = new VectorLayer({ source: vectorSourceRef.current });
            map.addLayer(vectorLayerRef.current);
        }
        vectorSourceRef.current.clear();
        const flightVolumes = [history[index].json];

        const features = ConvertCmpVolumesToFeatures(flightVolumes, userMapSettings);
        if (features && features.length > 0) {
            vectorSourceRef.current.addFeatures(features);

            const extent = vectorSourceRef.current.getExtent();
            map.getView().fit(extent, { padding: [50, 50, 50, 50] });
        }
    };
    const handleExportToKmlButtonClick = (json) => {
        try {
            handleDownloadOperations([json], userMapSettings);
        } catch (err) {
            console.error("An error occured while downloading this operation", err);
        }
    };
    const handleViewJsonButtonClick = (json) => {
        setSelectedFlightJson(json);
    };
    return (
        <Dialog
            open={selectedFlightHistoryFlight ? true : false}
            onClose={() => setSelectedFlightHistoryFlight(null)}
            maxWidth="sm"
            fullWidth
            slotProps={{ paper: { sx: { overflowX: "hidden" } } }}
        >
            <Slide in={!selectedFlightJson} appear={false} direction={"right"}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: !selectedFlightJson ? "100%" : 0,
                        width: !selectedFlightJson ? "100%" : 0,
                        overflow: "hidden"
                    }}
                >
                    <DialogTitle component="div" sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6">{selectedFlightHistoryFlight?.name}</Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {getDateRangeFromFlight(selectedFlightHistoryFlight)}
                                </Typography>
                            </Box>

                            <Tooltip title={"Close"} placement="left">
                                <IconButton size="small" onClick={() => setSelectedFlightHistoryFlight(null)}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box sx={{ width: "100%", height: "300px" }}>
                            <MapFullScreen map_id={MAP_ID} map={map} />
                        </Box>

                        {!flightHistoryErrorText ? (
                            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                                <Typography variant="h6">Flight History</Typography>

                                <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
                                    <IconButton
                                        size="small"
                                        disabled={selectedFlightHistoryEventIndex === 0}
                                        onClick={() => handleSelectedFlightIndexChange(selectedFlightHistoryEventIndex - 1, flightHistory)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </IconButton>

                                    <Typography variant="body1" color="text.secondary">
                                        {selectedFlightHistoryEventIndex + 1} of {flightHistory.length}
                                    </Typography>

                                    <IconButton
                                        disabled={selectedFlightHistoryEventIndex === flightHistory.length - 1}
                                        size="small"
                                        onClick={() => handleSelectedFlightIndexChange(selectedFlightHistoryEventIndex + 1, flightHistory)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        ) : (
                            <></>
                        )}
                    </DialogTitle>

                    <DialogContent sx={{ flex: 1, overflowY: "auto" }}>
                        {flightHistoryLoading === false && !flightHistoryErrorText ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                {flightHistory.map((event, index) => {
                                    const selected = selectedFlightHistoryEventIndex === index;
                                    return (
                                        <Box
                                            key={event.id}
                                            sx={{ position: "relative", display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}
                                            onClick={() => handleSelectedFlightIndexChange(index, flightHistory)}
                                            ref={selected ? selectedItemRef : null}
                                            data-testid={`event-${index}`}
                                        >
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 0,
                                                    top: 12,
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center"
                                                }}
                                            >
                                                {selected ? <RadioButtonCheckedIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                                                {index !== flightHistory.length - 1 ? (
                                                    <Box sx={{ height: "100%", width: "2px", backgroundColor: "white" }} />
                                                ) : (
                                                    <></>
                                                )}
                                            </Box>

                                            <Box
                                                sx={{ flex: 1, ml: 4, p: 1, border: selected ? "1px solid white" : "unset", borderRadius: 2, display: "flex" }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 1 }}>
                                                        <Typography variant="body1" color="text.primary">
                                                            {event.title}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {`${getHoursMinutesAndSecondsFromIsoString(event.time)}`}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" whiteSpace="pre-wrap">
                                                        {event.description}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                                                    {[
                                                        {
                                                            tooltip: "Download KML",
                                                            icon: <FileDownloadIcon fontSize="small" />,
                                                            onClick: () => handleExportToKmlButtonClick(event.json)
                                                        },
                                                        {
                                                            tooltip: "View JSON",
                                                            icon: <DataObjectIcon fontSize="small" />,
                                                            onClick: () => handleViewJsonButtonClick(event.json)
                                                        }
                                                    ].map(({ tooltip, icon, onClick }, i) => (
                                                        <Tooltip key={i} title={tooltip}>
                                                            <IconButton size="small" onClick={onClick}>
                                                                {icon}
                                                            </IconButton>
                                                        </Tooltip>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : flightHistoryLoading === true && !flightHistoryErrorText ? (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", justifyContent: "center" }}>
                                {[1, 2, 3].map((index) => (
                                    <Box key={index} sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
                                        <Skeleton variant="text" sx={{ fontSize: "1rem", width: "50%" }} />
                                        <Skeleton variant="text" sx={{ fontSize: "1rem", width: "75%" }} />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center", justifyContent: "center", p: 8 }}>
                                <ErrorIcon color="error" fontSize="large" />

                                <Box sx={{ display: "flex", flexDirection: "column" }}>
                                    <Typography variant="body1" color="text.primary" gutterBottom>
                                        An error occured
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary">
                                        We are currently unable to display this right now. Please try again later.
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Box>
            </Slide>

            <Slide in={selectedFlightJson} direction={"left"} unmountOnExit>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: selectedFlightJson ? "100%" : 0,
                        width: selectedFlightJson ? "100%" : 0,
                        overflow: "hidden"
                    }}
                >
                    <DialogTitle component="div" sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 1 }}>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Tooltip title={"Go back"}>
                                <IconButton size="small" onClick={() => setSelectedFlightJson(null)}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            </Tooltip>

                            <Box sx={{ flex: 1, pl: 2 }}>
                                <Typography variant="h6">{selectedFlightHistoryFlight?.name}</Typography>

                                <Typography variant="body1" color="text.secondary">
                                    {getDateRangeFromFlight(selectedFlightHistoryFlight)}
                                </Typography>
                            </Box>

                            <Tooltip title={"Close"}>
                                <IconButton size="small" onClick={() => setSelectedFlightHistoryFlight(null)} data-testid="closeJson">
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </DialogTitle>

                    <DialogContent sx={{ flex: 1, overflowY: "auto" }}>
                        <ReactJson
                            src={selectedFlightJson || {}}
                            shouldCollapse={({ name }) => name === "volumes"}
                            theme={"bright"}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            displayArrayKey={false}
                            style={{ backgroundColor: "unset", fontSize: "14px" }}
                        />
                    </DialogContent>
                </Box>
            </Slide>
        </Dialog>
    );
};

export default FlightHistoryDialog;
